/**
 * The three time series at the head of the Trendy tab — TONAŻ, SEN and ENERGIA.
 *
 * ── The canon's own rule, and the workspace's ─────────────────────────────────────────────────
 * "Odstępy między punktami odpowiadają odstępom między sesjami" and "dzień bez zgłoszenia nie jest
 * zerem — linia go pomija, zamiast zjeżdżać do podłogi". Both are honoured by the SHAPE of what
 * this returns: `x` is a real `Date`, and a wellbeing `y` is `number | null`.
 *
 * ── Two scales per wellbeing series, and the break behaviour is the WHOLE point ───────────────
 * Owner, 2 September 2026: "diagram snu zrób tak, by dało się przełączyć średnia tygodniowa vs
 * wyświetlać każdy pomiar (wtedy ma nie być przerwy w wykresie)", and then "dorzuć wykres
 * Energii/samopoczucia tak samo". So sleep and mood each carry two projections of ONE set of
 * facts, and which one is drawn is a parameter rather than a second entry point:
 *
 * | Scale | Points | Holes | Why |
 * |---|---|---|---|
 * | `weekly` | one per week in the window, `avgSleepHours` / `avgMood` | a week nobody reported is `null`, so the line BREAKS | the week exists whether or not it was answered, so its absence is a fact the grid can show |
 * | `readings` | one per reading the client actually logged | none, ever | there is no grid to leave a hole in — a reading only exists where somebody made one, so consecutive readings join and the line is continuous |
 *
 * The second row is the owner's sentence restated: a gap can only appear where something was
 * EXPECTED and did not arrive. `readings` plots the arrivals themselves, so nothing can be
 * missing from it. What still carries the information a gap used to carry is the x axis: two
 * readings a fortnight apart sit a fortnight apart, because `x` is the instant and never the index.
 *
 * ── One x domain across all three charts ──────────────────────────────────────────────────────
 * Owner, 2 September 2026: the three charts "mają leżeć na wspólnej osi czasu". Read side by side
 * they are one picture of one window, and three axes fitted to three different spans would put the
 * same date at three different pixels — which is the exact thing the shared crosshair exists to
 * deny. So the domain is decided ONCE here, as the union of the spans actually drawn, and every
 * chart is handed it. It is the union of what was REPORTED rather than the range the trainer
 * asked for: a series that starts halfway across the plot is saying the client began reporting
 * halfway through, which is true and worth seeing.
 *
 * ── Why tonnage keeps its zeros and the wellbeing series do not ───────────────────────────────
 * They are different kinds of absence and the contract already distinguishes them: a week with no
 * training has `tonnageKg: 0`, which is TRUE — the client lifted nothing — while `avgSleepHours`
 * and `avgMood` are `null`, because nobody reported. Plotting the first as zero is honest;
 * plotting the second as zero would say the client did not sleep and felt nothing.
 *
 * ── The summary is read off the series, not off a second field ────────────────────────────────
 * The card head prints `7,9 t  +1,5 vs poprzednia`. The contract publishes no such comparison, so
 * it is the last point against the one before it — and it is `null` when there is no "before",
 * rather than `+0` against an imagined empty week. Which "before" that is depends on the scale,
 * and the card has to say so in words, so `basis` travels with the series.
 *
 * ── A week that is still running is not a week that went badly ────────────────────────────────
 * The newest bucket is almost always the CURRENT week, and on a Tuesday it holds two days of work.
 * Compared against seven days of the week before it, that reads `−22,0 vs poprzednia` — a collapse
 * in training the trainer might act on, produced entirely by the calendar. The comparison is
 * withheld while the week is open and the card says the week is in progress instead. The FIGURE
 * stays: two days of tonnage is a true number, and it is the delta beside it that was the lie.
 *
 * **It applies to `weekly` only.** A reading is an instant, not a period: nothing about it is
 * still filling up, so on `readings` the comparison is always honest and the line's tail is never
 * drawn provisional.
 */

import { isReported, type ChartPoint } from '~/shared/lib/chartSeries';

/** Which projection of a wellbeing series is drawn. See the header's table. */
export type TrendScale = 'weekly' | 'readings';

export interface TrendPointSource {
  readonly weekStart: string;
  readonly label: string;
  readonly tonnageKg: number;
  readonly avgSleepHours: number | null;
  readonly avgMood: number | null;
}

/**
 * One reading as the client logged it, off `sleepReadings` / `moodReadings`.
 *
 * `at` is the instant the session finished — the backend charts them there and says so
 * (`CustomerWellbeingReading.FinishedAt`), because both values belong to the session rather than
 * to any work inside it.
 */
export interface WellbeingReadingSource {
  readonly at: string;
  readonly value: number;
}

export interface TrendsSource {
  readonly weeks: readonly TrendPointSource[];
  readonly sleepReadings: readonly WellbeingReadingSource[];
  readonly moodReadings: readonly WellbeingReadingSource[];
}

export interface TrendSeries {
  /**
   * The plotted series, oldest first. `shared/lib/chartSeries`'s `ChartPoint`, which is the one
   * point shape in this app rather than a fourth name for it.
   *
   * `x` is a real instant — the week's Monday, or the moment a reading was logged — never an index.
   * `y` of `null` is a GAP the line steps over, and on this tab only a `weekly` wellbeing series
   * can produce one.
   */
  readonly points: readonly ChartPoint[];
  /** The head figure: the newest point that carries a value, already formatted. */
  readonly latest: string | null;
  /** `+1,5` / `−0,4` against the previous point that carried one. `null` where there is no pair. */
  readonly delta: string | null;
  /** True when the delta is an improvement, for the ink. `null` where there is no delta. */
  readonly better: boolean | null;
  /**
   * What the delta compares against, so the card can name it. A weekly series compares two weeks;
   * a reading series compares two readings, and calling that "vs poprzednia [tydzień]" would be
   * a sentence about a period the chart is not drawing.
   */
  readonly basis: 'week' | 'reading';
  /**
   * The newest point's week has not finished yet, so it holds a partial total and `delta` is
   * withheld. The card says the week is still running rather than printing a comparison between a
   * Tuesday and a full week. Always false on a `readings` series — see the header.
   */
  readonly inProgress: boolean;
  /** True when nothing at all was reported — the card draws a sentence, not an empty grid. */
  readonly empty: boolean;
}

export interface TrendsView {
  readonly tonnage: TrendSeries;
  readonly sleep: TrendSeries;
  readonly mood: TrendSeries;
  /**
   * The span every chart on the tab is drawn over, so the same date lands on the same pixel in all
   * three. `null` when not one of them reported anything, which is when no chart is drawn at all.
   */
  readonly xDomain: readonly [Date, Date] | null;
}

export interface TrendsDeps {
  readonly locale: string;
  /** `t` is taken so the unit suffix is translated, never concatenated from a literal. */
  readonly t: (key: string, options?: Record<string, unknown>) => string;
  /** Injected so "is this week still running" is decided rather than read off a hidden clock. */
  readonly now?: Date;
  /** Which projection of the sleep series to draw. Defaults to the weekly average. */
  readonly sleepScale?: TrendScale;
  /** Which projection of the mood series to draw. Independent of sleep's — see `TrendsTab`. */
  readonly moodScale?: TrendScale;
}

function signed(value: number, locale: string): string {
  const magnitude = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.abs(value));
  // U+2212 MINUS SIGN, not a hyphen: the canon sets these figures tabular and a hyphen is narrower
  // than the plus it has to line up with.
  return value > 0 ? `+${magnitude}` : value < 0 ? `−${magnitude}` : magnitude;
}

const MS_PER_WEEK = 7 * 86_400_000;

/** Whether a week beginning at `weekStart` still has days left in it. */
function weekIsOpen(weekStart: Date, now: Date): boolean {
  const start = weekStart.getTime();
  if (Number.isNaN(start)) return false;
  return now.getTime() < start + MS_PER_WEEK;
}

function series(
  points: readonly ChartPoint[],
  format: (value: number) => string,
  locale: string,
  higherIsBetter: boolean,
  now: Date,
  basis: 'week' | 'reading',
): TrendSeries {
  const withValues = points.filter(
    (point): point is ChartPoint & { y: number } => point.y !== null,
  );
  const last = withValues.at(-1) ?? null;
  const previous = withValues.at(-2) ?? null;
  // The comparison is against a period that RAN. A newest bucket still filling up is two or three
  // days measured against seven, which is arithmetic about the calendar rather than about training.
  // A reading has no duration, so nothing about it can still be filling up.
  const inProgress = basis === 'week' && last !== null && weekIsOpen(last.x, now);
  const difference =
    last === null || previous === null || inProgress ? null : last.y - previous.y;

  return {
    points,
    latest: last === null ? null : format(last.y),
    delta: difference === null ? null : signed(difference, locale),
    better:
      difference === null || difference === 0 ? null : higherIsBetter === difference > 0,
    basis,
    inProgress,
    empty: withValues.length === 0,
  };
}

/**
 * A wellbeing series' points, at the scale asked for.
 *
 * The two branches differ in exactly one structural way and it is the one the owner asked about:
 * `weekly` walks the WEEK BUCKETS and carries their nulls through, so a week nobody answered is a
 * hole; `readings` walks the readings themselves, which cannot contain a hole because a reading
 * that was never taken is not in the list. Sorting is done here rather than trusted: the contract
 * orders oldest-first today, and a chart that trusts input order draws a zig-zag the day that
 * changes.
 */
function wellbeingPoints(
  scale: TrendScale,
  weeks: readonly TrendPointSource[],
  weekly: (week: TrendPointSource) => number | null,
  readings: readonly WellbeingReadingSource[],
): readonly ChartPoint[] {
  if (scale === 'weekly') {
    return weeks.map((week) => ({ x: new Date(week.weekStart), y: weekly(week) }));
  }
  return [...readings]
    .map((reading) => ({ x: new Date(reading.at), y: reading.value }))
    .sort((a, b) => a.x.getTime() - b.x.getTime());
}

/**
 * The span the three charts share.
 *
 * Only points that CARRY a value widen it: a trailing run of unreported weeks would otherwise
 * stretch every chart over dates nobody answered, which is the same defect `shared/lib/chartSeries`
 * trims for on a single series. `null` when nothing at all was reported anywhere.
 *
 * "Carries a value" is `chartSeries`'s own `isReported`, not a null check written again here. A
 * `NaN` passes a null check, so it used to widen this shared span while being trimmed away as a
 * hole inside every individual series — leaving all three charts on an axis none of them reached.
 */
function unionDomain(drawn: readonly TrendSeries[]): readonly [Date, Date] | null {
  let low = Number.POSITIVE_INFINITY;
  let high = Number.NEGATIVE_INFINITY;
  for (const one of drawn) {
    for (const point of one.points) {
      if (!isReported(point.y)) continue;
      const at = point.x.getTime();
      if (!Number.isFinite(at)) continue;
      if (at < low) low = at;
      if (at > high) high = at;
    }
  }
  return low > high ? null : ([new Date(low), new Date(high)] as const);
}

export function buildTrends(source: TrendsSource | null, deps: TrendsDeps): TrendsView {
  const { locale, t } = deps;
  const now = deps.now ?? new Date();
  const weeks = source?.weeks ?? [];
  const oneDecimal = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  const tonnage = series(
    // Kilograms on the wire, tonnes on the screen — the canon's axis is labelled `9,7 t`, and
    // mobile prints the same conversion, so the two apps quote one number.
    weeks.map((week) => ({ x: new Date(week.weekStart), y: week.tonnageKg / 1000 })),
    (value) => `${oneDecimal.format(value)} ${t('clients.detail.progress.tonnage.unit')}`,
    locale,
    true,
    now,
    'week',
  );

  const sleepScale = deps.sleepScale ?? 'weekly';
  const sleep = series(
    wellbeingPoints(
      sleepScale,
      weeks,
      (week) => week.avgSleepHours,
      source?.sleepReadings ?? [],
    ),
    (value) => `${oneDecimal.format(value)} ${t('clients.detail.progress.sleep.hour')}`,
    locale,
    true,
    now,
    sleepScale === 'weekly' ? 'week' : 'reading',
  );

  const moodScale = deps.moodScale ?? 'weekly';
  const mood = series(
    wellbeingPoints(moodScale, weeks, (week) => week.avgMood, source?.moodReadings ?? []),
    // No unit suffix. Mood is a 1-10 rating with no dimension, the card's caption names the scale,
    // and a `/ 10` on every figure would also have to fit the 46px axis gutter beside `7,3`.
    (value) => oneDecimal.format(value),
    locale,
    true,
    now,
    moodScale === 'weekly' ? 'week' : 'reading',
  );

  return { tonnage, sleep, mood, xDomain: unionDomain([tonnage, sleep, mood]) };
}
